import { z } from 'zod';

export const nonEmptyTrimmed = (label: string, max = 120) =>
  z
    .string({ required_error: `${label} is required` })
    .trim()
    .min(1, `${label} is required`)
    .max(max, `${label} must be at most ${max} characters`);

export const optionalTrimmed = (max = 120) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

export const emailSchema = z
  .string({ required_error: 'Email is required' })
  .trim()
  .toLowerCase()
  .email('Enter a valid email address')
  .max(254);

// UK mobile: strips spaces/parens/hyphens, must be exactly 11 digits starting 07.
export const phoneSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s()-]/g, ''))
  .pipe(
    z
      .string()
      .regex(/^07\d{9}$/, 'Enter a UK mobile number (11 digits starting 07)'),
  );

export const optionalPhone = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s()-]/g, ''))
  .optional()
  .transform((v) => (v === '' ? undefined : v))
  .refine((v) => v === undefined || /^07\d{9}$/.test(v), {
    message: 'Enter a UK mobile number (11 digits starting 07)',
  });

// dd/mm/yyyy → Date (verified to be a real calendar date, between 1900 and 2100).
export const dateDmySchema = z
  .string({ required_error: 'Date is required' })
  .trim()
  .regex(/^\d{2}\/\d{2}\/\d{4}$/, 'Enter the date as dd/mm/yyyy')
  .refine((s) => {
    const [d, m, y] = s.split('/').map((n) => Number(n));
    if (y === undefined || m === undefined || d === undefined) return false;
    if (y < 1900 || y > 2100) return false;
    const dt = new Date(Date.UTC(y, m - 1, d));
    return (
      dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
    );
  }, 'Enter a real calendar date');

// UK NI number. Accept with or without the display spacing.
export const niNumberSchema = z
  .string({ required_error: 'NI number is required' })
  .trim()
  .transform((v) => v.toUpperCase().replace(/\s+/g, ''))
  .pipe(
    z
      .string()
      .regex(
        /^(?!BG|GB|NK|KN|TN|NT|ZZ)[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z]\d{6}[A-D]$/,
        'Enter a valid National Insurance number',
      ),
  );

// UK postcode. Normalised to uppercase with a single space.
export const postcodeSchema = z
  .string({ required_error: 'Postcode is required' })
  .trim()
  .transform((v) => v.toUpperCase().replace(/\s+/g, ''))
  .pipe(
    z
      .string()
      .regex(
        /^(GIR 0AA|[A-PR-UWYZ]([0-9]{1,2}|([A-HK-Y][0-9]([0-9ABEHMNPRV-Y])?)|[0-9][A-HJKPS-UW])[0-9][ABD-HJLNP-UW-Z]{2})$/i,
        'Enter a valid UK postcode',
      )
      .transform((v) => `${v.slice(0, -3)} ${v.slice(-3)}`),
  );

// UK counties — combined list of English ceremonial counties, Scottish council
// areas, Welsh principal areas and Northern Irish traditional counties.
export const UK_COUNTIES = [
  'Aberdeen City',
  'Aberdeenshire',
  'Angus',
  'Antrim',
  'Argyll and Bute',
  'Armagh',
  'Bedfordshire',
  'Berkshire',
  'Blaenau Gwent',
  'Bridgend',
  'Bristol',
  'Buckinghamshire',
  'Caerphilly',
  'Cambridgeshire',
  'Cardiff',
  'Carmarthenshire',
  'Ceredigion',
  'Cheshire',
  'City of Edinburgh',
  'City of London',
  'Clackmannanshire',
  'Conwy',
  'Cornwall',
  'Cumbria',
  'Denbighshire',
  'Derbyshire',
  'Devon',
  'Dorset',
  'Down',
  'Dumfries and Galloway',
  'Dundee City',
  'Durham',
  'East Ayrshire',
  'East Dunbartonshire',
  'East Lothian',
  'East Renfrewshire',
  'East Riding of Yorkshire',
  'East Sussex',
  'Essex',
  'Falkirk',
  'Fermanagh',
  'Fife',
  'Flintshire',
  'Glasgow City',
  'Gloucestershire',
  'Greater London',
  'Greater Manchester',
  'Gwynedd',
  'Hampshire',
  'Herefordshire',
  'Hertfordshire',
  'Highland',
  'Inverclyde',
  'Isle of Anglesey',
  'Isle of Wight',
  'Kent',
  'Lancashire',
  'Leicestershire',
  'Lincolnshire',
  'Londonderry',
  'Merseyside',
  'Merthyr Tydfil',
  'Midlothian',
  'Monmouthshire',
  'Moray',
  'Na h-Eileanan Siar',
  'Neath Port Talbot',
  'Newport',
  'Norfolk',
  'North Ayrshire',
  'North Lanarkshire',
  'North Yorkshire',
  'Northamptonshire',
  'Northumberland',
  'Nottinghamshire',
  'Orkney Islands',
  'Oxfordshire',
  'Pembrokeshire',
  'Perth and Kinross',
  'Powys',
  'Renfrewshire',
  'Rhondda Cynon Taf',
  'Rutland',
  'Scottish Borders',
  'Shetland Islands',
  'Shropshire',
  'Somerset',
  'South Ayrshire',
  'South Lanarkshire',
  'South Yorkshire',
  'Staffordshire',
  'Stirling',
  'Suffolk',
  'Surrey',
  'Swansea',
  'Torfaen',
  'Tyne and Wear',
  'Tyrone',
  'Vale of Glamorgan',
  'Warwickshire',
  'West Dunbartonshire',
  'West Lothian',
  'West Midlands',
  'West Sussex',
  'West Yorkshire',
  'Wiltshire',
  'Worcestershire',
  'Wrexham',
] as const;

export const countySchema = z
  .enum(UK_COUNTIES)
  .optional()
  .or(z.literal('').transform(() => undefined));

export const addressSchema = z.object({
  houseNumber: nonEmptyTrimmed('House number or name', 60),
  line1: nonEmptyTrimmed('Address line 1', 200),
  line2: optionalTrimmed(200),
  town: nonEmptyTrimmed('Town', 120),
  county: countySchema,
  postcode: postcodeSchema,
});
export type Address = z.infer<typeof addressSchema>;

// S3 object key issued by our presign endpoint: uploads/<uuid>/<kind>.<ext>
export const uploadKeySchema = z
  .string()
  .trim()
  .regex(
    /^uploads\/[0-9a-f-]{36}\/(photoId|proofAddress|rightToWork|niProof|nameChange)\.[a-z0-9]{2,8}$/,
    'Invalid upload reference',
  );
